import React from 'react';
import { MotionConfig, motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Head from 'next/head';
import { useNotificationPreferencesForm } from '@hooks/notifications';

import { useIsAppView } from '@hooks/useIsAppView';
import { WEBSITE_NAME } from '@lib/constants';

import { ContextProvider, Header } from '@components/notifications';
import Form from '@components/notifications/Form';
import ConfirmationModal from '@components/notifications/ConfirmationModal';
import NotificationBox from '@components/notifications/NotificationBox';

interface NotificationPreferencesContainerProps {
  notificationPreferences?: Record<string, any>;
}

/**
 * Container component that manages state and passes props to the view
 */
const NotificationPreferencesContainer = React.memo(
  ({ notificationPreferences }: NotificationPreferencesContainerProps) => {
    const {
      formScope,
      animationControls,
      buttonScale,
      isModalOpen,
      modalType,
      isSubmitting,
      isPending,
      error,
      handleSubmit,
      handleConfirmModal,
      handleCloseModal,
      handleEnablePush,
      hasExistingPreferences,
      form,
      checkboxOptions,
      yesOptionProps,
      noOptionProps
    } = useNotificationPreferencesForm(notificationPreferences);

    const {
      register,
      setValue,
      formState: { errors },
      watch
    } = form;

    const notificationPreference = watch('notificationPreference');
    const hasEmailConsent = watch('hasEmailConsent') || false;
    const hasPushConsent = watch('hasPushConsent') || false;
    const { isAppView } = useIsAppView();
    const [inViewRef, isInView] = useInView({ triggerOnce: true });

    return (
      <MotionConfig transition={{ type: 'spring', duration: 0.4 }}>
        <Head>
          <title>Stay One Step Ahead | {WEBSITE_NAME}</title>
          <meta
            key="viewport"
            name="viewport"
            content="width=device-width, initial-scale=1, maximum-scale=1"
          />
        </Head>
        <div
          id="toast-container"
          className={`fixed top-0 ${
            isAppView ? 'left-1/2 -translate-x-1/2' : 'right-0'
          } flex flex-col p-6 gap-2 w-full md:max-w-[420px] m-0 z-[9999] outline-none`}
        />
        <main>
          <div
            className="flex flex-col justify-center py-6 sm:py-16 bg-gray-50 min-h-screen overflow-auto pb-20"
            role="main"
          >
            <div className="p-4 sm:p-6 w-full mx-auto">
              <Header
                headingControls={animationControls.heading}
                textControls={animationControls.text}
              />

              <div
                ref={inViewRef}
                className="mt-4 sm:mt-6 mx-auto w-full max-w-4xl"
              >
                <div className="rounded-lg bg-gradient-to-b from-[#F8FAFF] to-white shadow-[0_4px_8px_rgba(79,70,229,0.06)] border border-[#E2E8F0] overflow-hidden w-full sm:w-[85%] mx-auto">
                  <NotificationBox isInView={isInView} />

                  <div className="p-4 sm:p-6 bg-white shadow overflow-visible w-full">
                    <div className="space-y-6 w-full">
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4, duration: 0.4 }}
                      >
                        <h2 className="text-base sm:text-lg font-semibold text-gray-900 pb-2">
                          Are you ready to unlock your winning advantage?
                        </h2>
                        <hr className="h-px bg-gray-200 w-full border-0 mt-3" />
                      </motion.div>

                      <div className="mt-3 sm:mt-4">
                        <Form
                          isPending={isPending}
                          error={error}
                          formScope={formScope}
                          headingControls={animationControls.heading}
                          textControls={animationControls.text}
                          bgControls={animationControls.bg}
                          formControls={animationControls.form}
                          buttonScale={buttonScale}
                          notificationPreference={notificationPreference}
                          hasEmailConsent={hasEmailConsent}
                          hasPushConsent={hasPushConsent}
                          consentError={
                            (
                              errors as any
                            )?.consentOptions?.message?.toString() || null
                          }
                          setValue={setValue}
                          handleFormSubmit={handleSubmit}
                          isSubmitting={isSubmitting}
                          hasNotificationPreferences={hasExistingPreferences}
                          register={register}
                          watch={watch}
                          isAppView={isAppView}
                          checkboxOptions={checkboxOptions}
                          yesOptionProps={yesOptionProps}
                          noOptionProps={noOptionProps}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
        <ConfirmationModal
          isOpen={isModalOpen}
          type={modalType === 'email-only' ? 'email-only' : 'no-updates'}
          testId={
            modalType === 'email-only'
              ? 'push-confirmation-modal'
              : 'confirmation-modal'
          }
          onClose={
            modalType === 'email-only' ? handleEnablePush : handleCloseModal
          }
          onConfirm={handleConfirmModal}
        />
      </MotionConfig>
    );
  }
);

NotificationPreferencesContainer.displayName =
  'NotificationPreferencesContainer';

/**
 * Wrapper component that provides context
 */
const NotificationPreferencesPage = () => (
  <ContextProvider initialPreferences={undefined}>
    <NotificationPreferencesContainer notificationPreferences={undefined} />
  </ContextProvider>
);

export default NotificationPreferencesPage;
